import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
	Avatar,
	Badge,
	Button,
	Spinner,
	Text,
	tokens,
	makeStyles,
} from '@fluentui/react-components'
import {
	MicRegular,
	MicOffRegular,
	CallEndRegular,
	CallRegular,
} from '@fluentui/react-icons'
import { streamChat, type ChatMessage } from '../../services/chatService'
import { useEffects } from '../../contexts/EffectsContext'
import { BotAvatar } from '../common/BotAvatar'

type LangCode = 'en-US' | 'cs-CZ' | 'de-DE' | 'fr-FR' | 'pl-PL'

const LANG_NAMES: Record<LangCode, string> = {
	'en-US': 'English', 'cs-CZ': 'Czech', 'de-DE': 'German',
	'fr-FR': 'French', 'pl-PL': 'Polish',
}

const detectLangFromBrowser = (): LangCode => {
	const nav = navigator.language.toLowerCase()
	if (nav.startsWith('cs')) return 'cs-CZ'
	if (nav.startsWith('de')) return 'de-DE'
	if (nav.startsWith('fr')) return 'fr-FR'
	if (nav.startsWith('pl')) return 'pl-PL'
	return 'en-US'
}

// Detect language from transcribed text using character and word patterns.
// Called after each SR result so the next turn uses the correct lang.
const detectLangFromText = (text: string): LangCode => {
	// Polish: ł, ą, ę are distinctive
	if (/[łąę]/.test(text)) return 'pl-PL'
	// Czech: ř and ů appear only in Czech
	if (/[řů]/.test(text)) return 'cs-CZ'
	// German: ß or multiple umlauts
	if (/ß/.test(text) || (text.match(/[äöü]/gi)?.length ?? 0) >= 2) return 'de-DE'
	// French: ç, œ, or common French words
	if (/[çœ]/.test(text) || /\b(le|la|les|je|vous|nous|est|une|pour|avec)\b/i.test(text)) return 'fr-FR'
	// Czech: other diacritics (ě, š, č, ž, á, í, é, ú, ó, ý)
	if (/[ěščžáíéúóý]/.test(text)) return 'cs-CZ'
	// Polish: remaining Polish chars
	if (/[ćńśźż]/.test(text)) return 'pl-PL'
	// German common words
	if (/\b(und|ich|das|die|der|ein|nicht|mit|ist|haben|auf)\b/i.test(text)) return 'de-DE'
	return 'en-US'
}

const getBestVoice = (langCode: string): SpeechSynthesisVoice | null => {
	const voices = speechSynthesis.getVoices()
	const prefix = langCode.split('-')[0]
	return voices.find(v => v.lang === langCode)
		?? voices.find(v => v.lang.startsWith(prefix))
		?? null
}

const useStyles = makeStyles({
	inlinePanel: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '1rem',
		padding: '1.5rem 1rem',
		width: '100%',
		boxSizing: 'border-box',
	},
	status: {
		color: tokens.colorNeutralForeground3,
		textAlign: 'center',
	},
	transcript: {
		width: '100%',
		maxHeight: '160px',
		overflowY: 'auto',
		borderRadius: tokens.borderRadiusMedium,
		backgroundColor: tokens.colorNeutralBackground3,
		padding: '0.75rem',
		boxSizing: 'border-box',
		textAlign: 'left',
	},
	controls: {
		display: 'flex',
		gap: '1.25rem',
		alignItems: 'center',
	},
	callButton: {
		width: '56px',
		height: '56px',
		minWidth: '56px',
		borderRadius: '50%',
	},
})

type CallState = 'idle' | 'listening' | 'thinking' | 'speaking'

interface TranscriptLine {
	role: 'user' | 'assistant'
	text: string
}

interface VoiceCallProps {
	onClose: () => void
}

type AnyWindow = Window & typeof globalThis & Record<string, unknown>

const hasSpeechRecognition = (): boolean =>
	typeof window !== 'undefined' &&
	!!((window as AnyWindow).SpeechRecognition || (window as AnyWindow).webkitSpeechRecognition)

const getSpeechRecognition = () =>
	(window as AnyWindow).SpeechRecognition || (window as AnyWindow).webkitSpeechRecognition

type SRInstance = {
	lang: string; interimResults: boolean; maxAlternatives: number
	onresult: unknown; onerror: unknown; onend: unknown
	start(): void; stop(): void; abort(): void
}

export const VoiceCall: React.FC<VoiceCallProps> = ({ onClose }) => {
	const styles = useStyles()
	const [callState, setCallState] = useState<CallState>('idle')
	const [transcript, setTranscript] = useState<TranscriptLine[]>([])
	const [statusText, setStatusText] = useState('Press the mic to start')
	const [detectedLang, setDetectedLang] = useState<LangCode>(detectLangFromBrowser)
	const langRef = useRef<LangCode>(detectedLang)
	const callStateRef = useRef<CallState>('idle')
	const hangingUpRef = useRef<boolean>(false)
	const transcriptEndRef = useRef<HTMLDivElement>(null)
	const recognitionRef = useRef<SRInstance | null>(null)
	const abortRef = useRef<AbortController | null>(null)
	const historyRef = useRef<ChatMessage[]>([])

	const updateState = (s: CallState) => {
		callStateRef.current = s
		setCallState(s)
	}

	useEffect(() => {
		transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [transcript])

	useEffect(() => {
		// Reset on mount — React Strict Mode runs cleanup between double-invocations,
		// which would leave hangingUpRef.current=true for the real mount.
		hangingUpRef.current = false
		return () => {
			hangingUpRef.current = true
			abortRef.current?.abort()
			try { speechSynthesis.cancel() } catch { /* unavailable in some environments */ }
			recognitionRef.current?.abort()
		}
	}, [])

	const speak = useCallback((text: string): Promise<void> => {
		return new Promise((resolve) => {
			try {
				speechSynthesis.cancel()
				const utterance = new SpeechSynthesisUtterance(text)
				utterance.lang = langRef.current
				const voice = getBestVoice(langRef.current)
				if (voice) utterance.voice = voice
				utterance.rate = 1.05
				utterance.onend = () => resolve()
				utterance.onerror = () => resolve()
				speechSynthesis.speak(utterance)
			} catch {
				resolve()
			}
		})
	}, [])

	// Regular functions (not useCallback) — they only close over stable refs/setters,
	// so memoisation would provide no benefit and creates a circular dep problem.

	const startListening = () => {
		if (hangingUpRef.current) return
		if (!hasSpeechRecognition()) {
			setStatusText('Speech recognition not supported in this browser')
			return
		}

		const SR = getSpeechRecognition() as new () => SRInstance
		const recognition = new SR()
		recognitionRef.current = recognition
		recognition.lang = langRef.current
		recognition.interimResults = false
		recognition.maxAlternatives = 1

		recognition.onresult = (event: { results: { [x: number]: { [x: number]: { transcript: string } } } }) => {
			const text = event.results[0][0].transcript.trim()
			if (text) void sendToBot(text)
		}
		recognition.onerror = (event: { error?: string }) => {
			if (!hangingUpRef.current) {
				const msg = event.error === 'not-allowed'
					? 'Microphone access denied — check browser permissions'
					: event.error === 'no-speech'
						? 'No speech detected — try again'
						: 'Microphone error — try again'
				setStatusText(msg)
				updateState('idle')
			}
		}
		recognition.onend = () => {
			if (callStateRef.current === 'listening' && !hangingUpRef.current) {
				updateState('idle')
				setStatusText('Press the mic to speak')
			}
		}

		recognition.start()
		updateState('listening')
		setStatusText('Listening… speak now')
	}

	const sendToBot = async (userText: string) => {
		updateState('thinking')
		setStatusText('Thinking…')

		// Detect language from the transcription and update for next SR session + TTS.
		const detected = detectLangFromText(userText)
		if (detected !== langRef.current) {
			langRef.current = detected
			setDetectedLang(detected)
		}

		// Language hint appended to API payload so the bot responds in the right language.
		// The display text stays clean (no hint shown in transcript).
		const currentLang = langRef.current
		const langName = LANG_NAMES[currentLang]
		const apiContent = currentLang !== 'en-US'
			? `${userText}\n\n[Please respond in ${langName}]`
			: userText

		const userMsg: ChatMessage = { role: 'user', content: apiContent }
		const history: ChatMessage[] = [...historyRef.current, userMsg]
			.slice(-6)
			.map(m => ({
				role: m.role as ChatMessage['role'],
				content: m.role === 'assistant' && m.content.length > 300
					? m.content.slice(0, 300) + '…'
					: m.content,
			}))

		setTranscript(prev => [...prev, { role: 'user', text: userText }])
		abortRef.current = new AbortController()
		let reply = ''

		try {
			for await (const token of streamChat(history, abortRef.current.signal)) {
				reply += token
			}
		} catch {
			if (!hangingUpRef.current) {
				setStatusText('Something went wrong. Try again.')
				updateState('idle')
			}
			return
		}

		if (hangingUpRef.current) return

		if (!reply) {
			updateState('idle')
			setStatusText('Press the mic to speak')
			return
		}

		const assistantMsg: ChatMessage = { role: 'assistant', content: reply }
		historyRef.current = [...historyRef.current, userMsg, assistantMsg].slice(-6)
		setTranscript(prev => [...prev, { role: 'assistant', text: reply }])
		updateState('speaking')
		setStatusText('Speaking…')

		await speak(reply)

		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (hangingUpRef.current) return

		startListening()
	}

	const handleMicClick = () => {
		if (callState === 'listening') {
			recognitionRef.current?.stop()
			return
		}
		if (callState !== 'idle') return
		try { speechSynthesis.cancel() } catch { /* unavailable in some environments */ }
		startListening()
	}

	const handleHangUp = () => {
		hangingUpRef.current = true
		abortRef.current?.abort()
		try { speechSynthesis.cancel() } catch { /* unavailable in some environments */ }
		recognitionRef.current?.abort()
		onClose()
	}

	const micActive = callState === 'listening'
	const busy = callState === 'thinking' || callState === 'speaking'

	return (
		<div className={styles.inlinePanel} data-testid="voice-panel">
			<BotAvatar
				size={56}
				badge={{ status: callState === 'idle' ? 'available' : 'busy' }}
			/>
			<div style={{ textAlign: 'center' }}>
				<Text size={400} weight="semibold" block>
					Martin&apos;s CV
				</Text>
				<Badge appearance="filled" color="success" style={{ marginTop: '0.2rem' }}>
					AI Voice
				</Badge>
			</div>

			<Text size={300} className={styles.status}>
				{statusText}
				{detectedLang !== 'en-US' && (
					<span style={{ marginLeft: '0.4rem', opacity: 0.6, fontSize: '0.8em' }}>
						· {LANG_NAMES[detectedLang]}
					</span>
				)}
			</Text>

			{transcript.length > 0 && (
				<div className={styles.transcript}>
					{transcript.map((line, i) => (
						<Text key={i} size={200} block style={{
							marginBottom: '0.4rem',
							color: line.role === 'user'
								? tokens.colorNeutralForeground1
								: tokens.colorNeutralForeground2,
						}}>
							<strong>{line.role === 'user' ? 'You: ' : 'CV: '}</strong>
							{line.text}
						</Text>
					))}
					<div ref={transcriptEndRef} />
				</div>
			)}

			<div className={styles.controls}>
				<Button
					appearance={micActive ? 'primary' : 'outline'}
					icon={busy ? <Spinner size="tiny" /> : micActive ? <MicRegular /> : <MicOffRegular />}
					className={`${styles.callButton}${micActive ? ' fx-pulse' : ''}`}
					disabled={busy}
					onClick={handleMicClick}
					aria-label={micActive ? 'Stop listening' : 'Start listening'}
					style={micActive ? {
						backgroundColor: tokens.colorPaletteGreenBackground3,
						borderColor: tokens.colorPaletteGreenBorder2,
						color: tokens.colorPaletteGreenForeground2,
						borderWidth: '2px',
					} : {
						borderWidth: '2px',
					}}
				/>
				<Button
					appearance="outline"
					icon={<CallEndRegular />}
					className={styles.callButton}
					onClick={handleHangUp}
					aria-label="End call"
					style={{
						borderColor: tokens.colorPaletteRedBorder2,
						color: tokens.colorPaletteRedForeground2,
						borderWidth: '2px',
					}}
				/>
			</div>
		</div>
	)
}

// Standalone trigger button — keeps a floating modal option available
export const VoiceCallButton: React.FC<{ className?: string }> = ({ className }) => {
	const [open, setOpen] = useState(false)
	const { effects } = useEffects()
	return (
		<>
			<Button
				appearance="primary"
				icon={<CallRegular />}
				className={`${className ?? ''}${effects.ctaPulse ? ' fx-pulse' : ''}`}
				onClick={() => setOpen(true)}
				aria-label="Start voice call with Martin's CV"
			>
				Call my CV
			</Button>
			{open && (
				<div
					style={{
						position: 'fixed', inset: 0,
						backgroundColor: 'rgba(0,0,0,0.6)',
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						zIndex: 1200,
					}}
					role="dialog"
					aria-modal="true"
					aria-label="Voice call with Martin's CV"
				>
					<div style={{
						width: '340px', maxWidth: '90vw',
						borderRadius: tokens.borderRadiusXLarge,
						backgroundColor: tokens.colorNeutralBackground1,
						boxShadow: tokens.shadow64,
						overflow: 'hidden',
					}}>
						<VoiceCall onClose={() => setOpen(false)} />
					</div>
				</div>
			)}
		</>
	)
}

// Inline mode for use inside TeamsTab — no floating overlay
export const VoiceCallInline: React.FC<{ onClose: () => void }> = ({ onClose }) => {
	return <VoiceCall onClose={onClose} />
}

export default VoiceCall
